import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSubscriptionPlans } from '../hooks/useSubscriptions';
import { useClients } from '../hooks/useClients';

const { FiX, FiSave, FiLoader, FiCreditCard } = FiIcons;

const SubscriptionModal = ({ isOpen, onClose, subscription = null, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { plans } = useSubscriptionPlans();
  const { clients } = useClients();
  
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      client_id: '',
      plan_id: '',
      start_trial: false,
      auto_renew: true,
      payment_method: 'credit_card',
      discount_percentage: 0,
      custom_price: '',
      notes: ''
    }
  });

  const watchedPlanId = watch('plan_id');
  const watchedStartTrial = watch('start_trial');
  const watchedCustomPrice = watch('custom_price');

  const selectedPlan = plans.find(plan => plan.id.toString() === watchedPlanId);

  useEffect(() => {
    if (subscription) {
      reset({
        client_id: subscription.client_id?.toString() || '',
        plan_id: subscription.plan_id?.toString() || '',
        start_trial: subscription.is_trial || false,
        auto_renew: subscription.auto_renew || true,
        payment_method: subscription.payment_method || 'credit_card',
        discount_percentage: subscription.discount_percentage || 0,
        custom_price: subscription.custom_price || '',
        notes: subscription.notes || ''
      });
    } else {
      reset({
        client_id: '',
        plan_id: '',
        start_trial: false,
        auto_renew: true,
        payment_method: 'credit_card',
        discount_percentage: 0,
        custom_price: '',
        notes: ''
      });
    }
  }, [subscription, reset]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const client = clients.find(c => c.id.toString() === data.client_id);
      const plan = plans.find(p => p.id.toString() === data.plan_id);
      
      await onSubmit({
        ...data,
        client_id: parseInt(data.client_id),
        plan_id: parseInt(data.plan_id),
        discount_percentage: parseFloat(data.discount_percentage || 0),
        custom_price: data.custom_price ? parseFloat(data.custom_price) : null,
        client,
        plan
      });
      onClose();
    } catch (error) {
      console.error('Error submitting subscription:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateFinalPrice = () => {
    if (!selectedPlan) return 0;
    
    const basePrice = watchedCustomPrice ? parseFloat(watchedCustomPrice) : selectedPlan.price;
    const discount = parseFloat(watch('discount_percentage') || 0);
    
    return basePrice * (1 - discount / 100);
  };

  const paymentMethods = [
    { id: 'credit_card', name: 'Credit Card' },
    { id: 'bank_transfer', name: 'Bank Transfer' },
    { id: 'paypal', name: 'PayPal' },
    { id: 'stripe', name: 'Stripe' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {subscription ? 'Edit Subscription' : 'Create New Subscription'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {subscription ? 'Update subscription details' : 'Set up a new subscription for a client'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  disabled={isSubmitting}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
              {/* Client and Plan Selection */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiUsers} className="w-5 h-5 mr-2" />
                  Client & Plan Selection
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <select
                      {...register('client_id', { required: 'Client is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={!!subscription}
                    >
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.business_name} ({client.name})
                        </option>
                      ))}
                    </select>
                    {errors.client_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.client_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Plan *
                    </label>
                    <select
                      {...register('plan_id', { required: 'Plan is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Plan</option>
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {formatCurrency(plan.price)}/{plan.billing_cycle}
                        </option>
                      ))}
                    </select>
                    {errors.plan_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.plan_id.message}</p>
                    )}
                  </div>
                </div>

                {selectedPlan && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-blue-300">
                    <h4 className="font-semibold text-gray-700 mb-2">Selected Plan Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><span className="font-medium">Name:</span> {selectedPlan.name}</p>
                        <p><span className="font-medium">Price:</span> {formatCurrency(selectedPlan.price)}</p>
                        <p><span className="font-medium">Billing:</span> {selectedPlan.billing_cycle}</p>
                      </div>
                      <div>
                        <p><span className="font-medium">Trial Days:</span> {selectedPlan.trial_days}</p>
                        <p><span className="font-medium">Category:</span> {selectedPlan.category}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="font-medium text-gray-700">Features:</p>
                      <ul className="text-sm text-gray-600 list-disc list-inside">
                        {selectedPlan.features?.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Trial Settings */}
              {selectedPlan && selectedPlan.trial_days > 0 && !subscription && (
                <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <SafeIcon icon={FiIcons.FiClock} className="w-5 h-5 mr-2" />
                    Trial Period
                  </h3>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        {...register('start_trial')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Start with {selectedPlan.trial_days}-day free trial
                      </span>
                    </label>
                  </div>
                  
                  {watchedStartTrial && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-green-300">
                      <p className="text-sm text-green-800">
                        <strong>Trial Period:</strong> {selectedPlan.trial_days} days free, 
                        then {formatCurrency(calculateFinalPrice())} per {selectedPlan.billing_cycle}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Billing Settings */}
              <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiCreditCard} className="w-5 h-5 mr-2" />
                  Billing Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      {...register('payment_method')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        {...register('auto_renew')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Auto-renew subscription
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Pricing Adjustments */}
              <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiDollarSign} className="w-5 h-5 mr-2" />
                  Pricing Adjustments
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('discount_percentage', {
                        min: { value: 0, message: 'Discount cannot be negative' },
                        max: { value: 100, message: 'Discount cannot exceed 100%' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="0"
                    />
                    {errors.discount_percentage && (
                      <p className="text-red-500 text-sm mt-1">{errors.discount_percentage.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Price (override plan price)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('custom_price', {
                        min: { value: 0, message: 'Price cannot be negative' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder={selectedPlan ? formatCurrency(selectedPlan.price) : '0.00'}
                    />
                    {errors.custom_price && (
                      <p className="text-red-500 text-sm mt-1">{errors.custom_price.message}</p>
                    )}
                  </div>
                </div>

                {selectedPlan && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-yellow-300">
                    <h4 className="font-semibold text-gray-700 mb-2">Pricing Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Base Price:</span>
                        <span>{formatCurrency(selectedPlan.price)}</span>
                      </div>
                      {watchedCustomPrice && (
                        <div className="flex justify-between">
                          <span>Custom Price:</span>
                          <span>{formatCurrency(watchedCustomPrice)}</span>
                        </div>
                      )}
                      {watch('discount_percentage') > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount ({watch('discount_percentage')}%):</span>
                          <span>-{formatCurrency((watchedCustomPrice || selectedPlan.price) * (watch('discount_percentage') / 100))}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Final Price:</span>
                        <span>{formatCurrency(calculateFinalPrice())} / {selectedPlan.billing_cycle}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Additional Notes
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    {...register('notes')}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional notes about this subscription..."
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{subscription ? 'Update Subscription' : 'Create Subscription'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SubscriptionModal;